import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
// V2 iOS 26 Pages (NEW UI)
import DashboardV2 from './pages/DashboardV2';
// P1 FIX: ProjectCalendarV2 removed - redirecting to CalendarStep
// Legacy pages still in use
import ContentEditor from './pages/ContentEditor';
import ProjectSettings from './pages/ProjectSettings';
import NewProject from './pages/NewProject';
import ProjectAnalytics from './pages/ProjectAnalytics';
import EditProject from './pages/EditProject';
import Integrations from './pages/Integrations';
import LandingPageV2 from './pages/LandingPageV2';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletionPage from './pages/DataDeletionPage';
import EmailGrowthCalendar from './pages/EmailGrowthCalendar';
import SupportDiagnostics from './pages/SupportDiagnostics';
// P0 FIX: OAuth X callback landing page (prevents white screen)
import OAuthXComplete from './pages/OAuthXComplete';
// P0 NEW: Social Setup Expert Demo
import ExpertDemo from './pages/ExpertDemo';
// P0 CRM: Prospecting Agent
import ProspectingAgent from './pages/ProspectingAgent';
// Guided Setup Pages (iOS 26 Design)
import AnalyzeStep from './pages/guided/AnalyzeStep';
import ConnectStep from './pages/guided/ConnectStep';
import CampaignStep from './pages/guided/CampaignStep';
import GenerateStep from './pages/guided/GenerateStep';
import CalendarStep from './pages/guided/CalendarStep';
import ResultsStep from './pages/guided/ResultsStep';
import DeleteStep from './pages/guided/DeleteStep';
// Layouts
import ProjectLayout from './components/ios26/ProjectLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';

const PrivateRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/auth" />;
};

// P1 FIX: Redirect component that uses useParams for correct path
const RedirectToSetupCalendar = () => {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}/setup/calendar`} replace />;
};

function AppContent() {
  const { token } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPageV2 />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* P0 FIX: OAuth X callback landing page - PUBLIC route (prevents white screen on Safari iOS) */}
        <Route path="/oauth/x/complete" element={<OAuthXComplete />} />
        
        {/* P0 NEW: Social Setup Expert Demo - PUBLIC route for testing */}
        <Route path="/demo/expert" element={<ExpertDemo />} />
        
        {/* Auth - Support both /auth and /login routes */}
        <Route path="/auth" element={!token ? <AuthPage /> : <Navigate to="/dashboard" />} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        
        {/* ============================================
            PROTECTED ROUTES - iOS 26 NEW UI
            ============================================ */}
        
        {/* Dashboard - iOS 26 */}
        <Route path="/dashboard" element={<PrivateRoute><DashboardV2 /></PrivateRoute>} />
        
        {/* P0 CRM: Prospecting Agent */}
        <Route path="/prospecting" element={<PrivateRoute><ProspectingAgent /></PrivateRoute>} />
        
        {/* New Project */}
        <Route path="/projects/new" element={<PrivateRoute><NewProject /></PrivateRoute>} />
        
        {/* P1 FIX: Redirect legacy calendar to new guided calendar */}
        <Route path="/projects/:projectId/calendar" element={
          <RedirectToSetupCalendar />
        } />
        
        <Route path="/projects/:projectId/content/:contentId" element={
          <PrivateRoute>
            <ProjectLayout>
              <ContentEditor />
            </ProjectLayout>
          </PrivateRoute>
        } />
        
        <Route path="/projects/:projectId/settings" element={
          <PrivateRoute>
            <ProjectLayout>
              <ProjectSettings />
            </ProjectLayout>
          </PrivateRoute>
        } />
        
        <Route path="/projects/:projectId/analytics" element={
          <PrivateRoute>
            <ProjectLayout>
              <ProjectAnalytics />
            </ProjectLayout>
          </PrivateRoute>
        } />
        
        <Route path="/projects/:projectId/edit" element={
          <PrivateRoute>
            <ProjectLayout>
              <EditProject />
            </ProjectLayout>
          </PrivateRoute>
        } />
        
        {/* Guided Setup Flow (iOS 26 Design) */}
        <Route path="/projects/:projectId/setup" element={<Navigate to="analyze" replace />} />
        <Route path="/projects/:projectId/setup/analyze" element={<PrivateRoute><AnalyzeStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/connect" element={<PrivateRoute><ConnectStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/campaign" element={<PrivateRoute><CampaignStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/generate" element={<PrivateRoute><GenerateStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/calendar" element={<PrivateRoute><CalendarStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/results" element={<PrivateRoute><ResultsStep /></PrivateRoute>} />
        <Route path="/projects/:projectId/setup/delete" element={<PrivateRoute><DeleteStep /></PrivateRoute>} />
        
        {/* Utility routes */}
        <Route path="/integrations" element={<PrivateRoute><Integrations /></PrivateRoute>} />
        <Route path="/email-growth" element={<PrivateRoute><EmailGrowthCalendar /></PrivateRoute>} />
        <Route path="/settings/support/diagnostics" element={<PrivateRoute><SupportDiagnostics /></PrivateRoute>} />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;